import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { X } from "lucide-react";
import { useVideoPlayer } from "./VideoPlayerProvider";

const HULU_URL =
  "https://www.hulu.com/movie/the-real-matryoshka-a-documentary-by-karime-pindter-3cbf29ee-4f60-42f5-b686-412c15642120";

const DISNEY_URL =
  "https://www.disneyplus.com/browse/entity-3cbf29ee-4f60-42f5-b686-412c15642120";

const POSTER_URL =
  "https://res.cloudinary.com/dbvqeiqxb/image/upload/v1792427540/IMG_1360_t2pfcz.jpg";

const TRAILER_ID = "OP04XT82eNo";

const DocumentalBanner = () => {
  const { play } = useVideoPlayer();
  const navigate = useNavigate();
  const [showModal, setShowModal] = useState(false);

  const [ripples, setRipples] = useState([]);

  const handleBannerClick = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    const id = Date.now();
    setRipples((r) => [...r, { x, y, id }]);
    setTimeout(() => setRipples((r) => r.filter((rip) => rip.id !== id)), 550);
    setTimeout(() => setShowModal(true), 200);
  };

  return (
    <>
      <style>{`
        @keyframes docGlow {
          0%, 100% { box-shadow: 0 2px 10px rgba(212,107,224,0.3); }
          50%       { box-shadow: 0 2px 18px rgba(212,107,224,0.6); }
        }
        @keyframes ripple {
          to { transform: scale(4); opacity: 0; }
        }
        .ripple-btn { position: relative; overflow: hidden; }
        .ripple-btn .ripple {
          position: absolute;
          border-radius: 50%;
          width: 80px; height: 80px;
          margin-top: -40px; margin-left: -40px;
          background: rgba(255,255,255,0.25);
          transform: scale(0);
          animation: ripple 0.5s linear;
          pointer-events: none;
        }
        .doc-glow { animation: docGlow 2.5s ease-in-out infinite; }
      `}</style>

      <button
        onClick={handleBannerClick}
        className="ripple-btn doc-glow w-full flex items-center gap-3 px-4 py-2 border-b border-white/10 font-quicksand"
        style={{ background: "linear-gradient(135deg, #3d1060, #1a0a2e)" }}
      >
        {ripples.map(({ x, y, id }) => (
          <span key={id} className="ripple" style={{ left: x, top: y }} />
        ))}
        <span style={{ fontSize: "15px" }}>🎬</span>

        <div className="text-left flex-1">
          <p className="text-white font-bold text-sm leading-tight">
            La Verdadera Matryoshka
          </p>
          <p className="text-purple-300 text-[10px]">
            Ya disponible en Hulu y Hulu en Disney+ · Solo en EUA 🇺🇸
          </p>
        </div>
        <span className="text-purple-300 text-lg">›</span>
      </button>

      {/* Modal con poster */}
      {showModal && (
        <div
          className="fixed inset-0 z-50 flex items-end justify-center"
          style={{ background: "rgba(0,0,0,0.75)" }}
          onClick={() => setShowModal(false)}
        >
          <div
            className="w-full font-quicksand rounded-t-3xl overflow-hidden mb-0"
            style={{
              background: "#1a0a2e",
              border: "1px solid rgba(212,160,224,0.25)",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Poster */}
            <div className="relative overflow-hidden" style={{ height: "48vh" }}>
              {/* Fondo difuminado para rellenar los lados sin recortar el poster */}
              <img
                src={POSTER_URL}
                alt=""
                aria-hidden="true"
                className="absolute inset-0 w-full h-full object-cover"
                style={{ filter: "blur(18px) brightness(0.6)", transform: "scale(1.2)" }}
              />
              <img
                src={POSTER_URL}
                alt="La Verdadera Matryoshka"
                className="relative w-full h-full object-contain"
              />
              {/* Gradient overlay sobre el poster para que el texto de abajo se integre */}
              <div
                className="absolute bottom-0 left-0 right-0 h-24"
                style={{
                  background:
                    "linear-gradient(to bottom, transparent, #1a0a2e)",
                }}
              />
              {/* Badge encima del poster */}
              <div className="absolute top-3 left-3">
                <span
                  className="text-xs font-bold tracking-widest uppercase px-3 py-1 rounded-full"
                  style={{ background: "#D46BE0", color: "white" }}
                >
                  ¡ya en Hulu!
                </span>
              </div>
              {/* X para cerrar — esquina superior derecha del poster */}
              <button
                onClick={() => setShowModal(false)}
                className="absolute top-3 right-3 w-8 h-8 rounded-full flex items-center justify-center active:scale-90 transition-transform"
                style={{ background: "rgba(0,0,0,0.5)" }}
              >
                <X size={16} color="white" />
              </button>
            </div>

            {/* Info */}
            <div className="px-6 pb-10 flex flex-col gap-3 mt-2">
              <a
                href={HULU_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-3 rounded-xl p-3 active:opacity-70 transition-opacity"
                style={{ background: "rgba(255,255,255,0.07)" }}
              >
                <span style={{ fontSize: "18px" }}>🎬</span>
                <p className="text-white text-sm leading-snug flex-1">
                  <span style={{ color: "#D46BE0" }} className="font-bold">
                    ¡Ya disponible!
                  </span>
                  {" · En "}
                  <span style={{ color: "#D46BE0" }} className="font-bold">
                    Hulu
                  </span>
                  {" y "}
                  <span style={{ color: "#D46BE0" }} className="font-bold">
                    Hulu en Disney+
                  </span>
                  {" · Solo en EUA 🇺🇸"}
                </p>
                <span style={{ color: "#D46BE0", fontSize: 14 }}>{"→"}</span>
              </a>

              <p className="text-purple-300 text-xs italic leading-relaxed border-l-2 border-purple-400 pl-3">
                "¿En qué me metí?" — Ensayó como una loca ante 20 mil personas
                para demostrar que podía romper sus propios límites. — Us
                Magazine
              </p>

              <button
                onClick={() => {
                  play(TRAILER_ID);
                  setShowModal(false);
                }}
                className="w-full py-3 rounded-full font-bold text-sm text-white active:scale-95 transition-transform block text-center"
                style={{ background: "#D46BE0" }}
              >
                ▶ Ver trailer
              </button>

              <div className="flex gap-3">
                {[
                  ["📺 Ver en Hulu", HULU_URL],
                  ["✨ Ver en Disney+", DISNEY_URL],
                ].map(([label, url]) => (
                  <a
                    key={label}
                    href={url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex-1 py-3 rounded-full font-bold text-sm text-center block active:scale-95 transition-transform"
                    style={{
                      background: "transparent",
                      border: "2px solid #D46BE0",
                      color: "#D46BE0",
                    }}
                  >
                    {label}
                  </a>
                ))}
              </div>

              <button
                onClick={() => {
                  setShowModal(false);
                  navigate("/ayuda-vpn");
                }}
                className="text-purple-300 text-xs underline text-center active:opacity-70"
              >
                ¿No estás en EUA? Mira cómo verlo con una VPN
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default DocumentalBanner;
